/**
 * Centralized Permission Types & Role-Permission Mapping
 *
 * Holographic Universe Principle: Every permission check contains the full picture —
 * role, capabilities, org context, and functional roles.
 *
 * Economy of Movement: Single import for any permission check.
 * Gravitational Organisation: Permissions naturally orbit their roles.
 *
 * @module permissions
 * @since 2026-02-21
 */

// ============================================================================
// PERMISSION DOMAINS (aligned with EventDomain for mycelium consistency)
// ============================================================================

/**
 * RBAC permission domains.
 *
 * Aligned (where applicable) with `EventDomain` in `packages/mycelium-core`:
 * - Matching:     'sales' | 'accounting' | 'crm' | 'ai' | 'calendar'
 * - Permission-only (no events): 'admin' | 'tasks' | 'organizations'
 * - Event alias:  EventDomain 'task' maps to PermissionDomain 'tasks'
 * - Shared:       'community' | 'documents'
 *
 * When adding a domain here, evaluate whether a corresponding EventDomain
 * is needed in `packages/mycelium-core/src/types/backbone.ts`.
 */
export type PermissionDomain =
  | 'admin'
  | 'sales'
  | 'tasks'
  | 'accounting'
  | 'crm'
  | 'ai'
  | 'community'
  | 'calendar'
  | 'documents'
  | 'organizations';

// ============================================================================
// PERMISSIONS (action:resource pattern)
// ============================================================================

/**
 * Granular permissions following action:resource convention.
 * Each permission is a capability, not a role.
 */
export type Permission =
  // Admin
  | 'manage:users'
  | 'manage:settings'
  | 'manage:billing'
  | 'manage:roles'
  | 'view:admin_dashboard'
  // Data visibility
  | 'view:all_data'
  | 'view:team_data'
  | 'view:own_data'
  // Team management
  | 'manage:team'
  | 'approve:requests'
  | 'view:team_analytics'
  // Tasks
  | 'create:tasks'
  | 'edit:own_tasks'
  | 'edit:all_tasks'
  | 'assign:tasks'
  | 'delete:tasks'
  | 'view:all_tasks'
  // CRM / Sales
  | 'manage:opportunities'
  | 'manage:commissions'
  | 'manage:territories'
  | 'manage:seasons'
  | 'view:sales_analytics'
  // Accounting
  | 'manage:invoices'
  | 'manage:transactions'
  | 'manage:subscriptions'
  // Calendar
  | 'manage:bookings'
  | 'manage:calendar_settings'
  | 'create:meetings'
  // AI
  | 'access:ai_agent'
  | 'manage:quick_actions'
  | 'manage:ai_settings'
  // Community
  | 'moderate:community'
  | 'manage:comments'
  // Organizations
  | 'manage:organizations'
  | 'share:resources'
  // Documents
  | 'create:documents'
  | 'manage:templates';

// ============================================================================
// AUTHORITY ROLE → PERMISSION MAPPING (Single Source of Truth)
// ============================================================================

/**
 * Maps authority roles (from user_roles table) to their permissions.
 * This is the gravitational centre — all role checks orbit this mapping.
 */
export const ROLE_PERMISSIONS: Record<string, readonly Permission[]> = {
  admin: [
    'manage:users', 'manage:settings', 'manage:billing', 'manage:roles',
    'view:admin_dashboard', 'view:all_data', 'view:team_data', 'view:own_data',
    'manage:team', 'approve:requests', 'view:team_analytics',
    'create:tasks', 'edit:own_tasks', 'edit:all_tasks', 'assign:tasks', 'delete:tasks', 'view:all_tasks',
    'manage:opportunities', 'manage:commissions', 'manage:territories', 'manage:seasons', 'view:sales_analytics',
    'manage:invoices', 'manage:transactions', 'manage:subscriptions',
    'manage:bookings', 'manage:calendar_settings', 'create:meetings',
    'access:ai_agent', 'manage:quick_actions', 'manage:ai_settings',
    'moderate:community', 'manage:comments',
    'manage:organizations', 'share:resources',
    'create:documents', 'manage:templates',
  ],
  moderator: [
    'view:admin_dashboard', 'view:all_data', 'view:team_data', 'view:own_data',
    'manage:team', 'approve:requests', 'view:team_analytics',
    'create:tasks', 'edit:own_tasks', 'edit:all_tasks', 'assign:tasks', 'view:all_tasks',
    'manage:opportunities', 'view:sales_analytics',
    'manage:bookings', 'create:meetings',
    'access:ai_agent', 'manage:quick_actions',
    'moderate:community', 'manage:comments',
    'share:resources',
    'create:documents',
  ],
  architect: [
    'view:admin_dashboard', 'view:all_data', 'view:team_data', 'view:own_data',
    'manage:settings',
    'create:tasks', 'edit:own_tasks', 'edit:all_tasks', 'assign:tasks', 'view:all_tasks',
    'access:ai_agent', 'manage:quick_actions', 'manage:ai_settings',
    'create:documents', 'manage:templates',
  ],
  manager: [
    'view:team_data', 'view:own_data',
    'manage:team', 'approve:requests', 'view:team_analytics',
    'create:tasks', 'edit:own_tasks', 'assign:tasks', 'view:all_tasks',
    'manage:opportunities', 'manage:commissions', 'view:sales_analytics',
    'manage:bookings', 'create:meetings',
    'access:ai_agent', 'manage:quick_actions',
    'share:resources',
    'create:documents',
  ],
  member: [
    'view:own_data',
    'create:tasks', 'edit:own_tasks',
    'create:meetings',
    'access:ai_agent',
    'create:documents',
  ],
  viewer: [
    'view:own_data',
  ],
} as const;

// ============================================================================
// PERMISSION CONTEXT (Holographic — each node carries full picture)
// ============================================================================

/**
 * Complete permission context for a user.
 * Like a hologram — each piece contains the whole picture.
 */
export interface PermissionContext {
  userId: string;
  /** Authority roles from user_roles table */
  authorityRoles: string[];
  /** Functional roles from functional_roles table */
  functionalRoles: string[];
  /** Resolved permissions (merged from all roles) */
  permissions: Permission[];
  /** Organization context */
  organizationId?: string;
  /** Team context */
  teamId?: string;
  /** When this context was resolved */
  resolvedAt: number;
}

// ============================================================================
// HELPER FUNCTIONS (Economy of Movement — one call does the work)
// ============================================================================

/**
 * Resolve all permissions for a set of roles.
 * Merges permissions from all roles with deduplication.
 */
export function resolvePermissions(roles: string[]): Permission[] {
  const permissionSet = new Set<Permission>();
  for (const role of roles) {
    const rolePerms = ROLE_PERMISSIONS[role];
    if (rolePerms) {
      for (const perm of rolePerms) {
        permissionSet.add(perm);
      }
    }
  }
  return Array.from(permissionSet);
}

/**
 * Check if a set of roles grants a specific permission.
 * Economy of movement — single function, no service dependency needed.
 */
export function hasPermission(roles: string[], permission: Permission): boolean {
  for (const role of roles) {
    const rolePerms = ROLE_PERMISSIONS[role];
    if (rolePerms && rolePerms.includes(permission)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a set of roles grants ANY of the specified permissions.
 */
export function hasAnyPermission(roles: string[], permissions: Permission[]): boolean {
  return permissions.some(p => hasPermission(roles, p));
}

/**
 * Check if a set of roles grants ALL of the specified permissions.
 */
export function hasAllPermissions(roles: string[], permissions: Permission[]): boolean {
  return permissions.every(p => hasPermission(roles, p));
}

/**
 * Get the permission domain for a permission string.
 * Useful for filtering/grouping permissions by area.
 */
export function getPermissionDomain(permission: Permission): PermissionDomain {
  const domainMap: Record<string, PermissionDomain> = {
    'manage:users': 'admin', 'manage:settings': 'admin', 'manage:billing': 'admin',
    'manage:roles': 'admin', 'view:admin_dashboard': 'admin',
    'view:all_data': 'admin', 'view:team_data': 'admin', 'view:own_data': 'admin',
    'manage:team': 'admin', 'approve:requests': 'admin', 'view:team_analytics': 'admin',
    'create:tasks': 'tasks', 'edit:own_tasks': 'tasks', 'edit:all_tasks': 'tasks',
    'assign:tasks': 'tasks', 'delete:tasks': 'tasks', 'view:all_tasks': 'tasks',
    'manage:opportunities': 'crm', 'manage:commissions': 'sales',
    'manage:territories': 'sales', 'manage:seasons': 'sales', 'view:sales_analytics': 'sales',
    'manage:invoices': 'accounting', 'manage:transactions': 'accounting',
    'manage:subscriptions': 'accounting',
    'manage:bookings': 'calendar', 'manage:calendar_settings': 'calendar',
    'create:meetings': 'calendar',
    'access:ai_agent': 'ai', 'manage:quick_actions': 'ai', 'manage:ai_settings': 'ai',
    'moderate:community': 'community', 'manage:comments': 'community',
    'manage:organizations': 'organizations', 'share:resources': 'organizations',
    'create:documents': 'documents', 'manage:templates': 'documents',
  };
  return domainMap[permission] || 'admin';
}
